import React, { useEffect, useState, useMemo } from "react";
import { Row, Col, Card, Badge } from "react-bootstrap";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from "react-chartjs-2";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import dateFormat from "dateformat";
import Api from "../../../services/Api";
import { WiHumidity, WiThermometer } from "react-icons/wi";
import { TbPackage } from "react-icons/tb";

// Registrar os componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels
);

const plugins = {
  afterDraw: function (chart) {
    if (chart.data.datasets.length > 0) {
      if (chart.data.datasets[0].data.length === 0) {
        var width = chart.width,
          height = chart.height,
          ctx = chart.ctx;
        ctx.restore();
        var fontSize = (height / 160).toFixed(2);
        ctx.font = fontSize + "em sans-serif";
        ctx.textBaseline = "top";
        var text = "Nenhuma Informação Disponível",
          textX = Math.round((width - ctx.measureText(text).width) / 2),
          textY = height / 2;
        ctx.fillText(text, textX, textY);
        ctx.save();
      }
    }
  },
}

let delayed;

const options = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    y: {
      beginAtZero: false,
      ticks: {
        precision: 1,
        color: '#b0b0b0',
        font: {
          size: 11
        }
      },
      grid: {
        color: 'rgba(255, 255, 255, 0.1)',
        borderDash: [5, 5]
      }
    },
    x: {
      ticks: {
        maxRotation: 45,
        minRotation: 45,
        color: '#b0b0b0',
        font: {
          size: 10
        }
      },
      grid: {
        display: false
      }
    },
  },
  animations: {
    radius: {
      duration: 500,
      easing: 'linear',
      loop: (context) => context.active
    }
  },
  animation: {
    onComplete: () => {
      delayed = true;
    },
    delay: (context) => {
      let delay = 0;
      if (context.type === 'data' && context.mode === 'default' && !delayed) {
        delay = context.dataIndex * 5 + context.datasetIndex * 2;
      }
      return delay;
    },
  },
  hoverRadius: 8,
  hoverBackgroundColor: '#a4c639',
  interaction: {
    mode: 'nearest',
    intersect: false,
    axis: 'x'
  },
  plugins: {
    legend: {
      display: true,
      position: 'top',
      labels: {
        color: '#e0e0e0',
        boxWidth: 15,
        font: {
          size: 11
        }
      }
    },
    tooltip: {
      enabled: true,
      backgroundColor: 'rgba(40, 44, 52, 0.9)',
      titleColor: '#e0e0e0',
      bodyColor: '#e0e0e0',
      titleFont: {
        size: 12
      },
      bodyFont: {
        size: 11
      },
      padding: 10,
      cornerRadius: 8,
      displayColors: false,
      borderColor: 'rgba(164, 198, 57, 0.3)',
      borderWidth: 1
    },
    title: {
      display: true,
      text: '',
      font: {
        size: 14,
        weight: 'bold'
      },
      color: '#e0e0e0'
    },
    datalabels: {
      color: '#e0e0e0',
      font: {
        weight: 'bold',
        size: 11
      },
      formatter: (value) => value ? value : '',
      align: 'end',
      offset: 0,
      backgroundColor: 'rgba(40, 44, 52, 0.7)',
      borderRadius: 4,
      padding: 2
    }
  }
};

let width, height, gradient;
function getGradient(ctx, chartArea) {
  const chartWidth = chartArea.right - chartArea.left;
  const chartHeight = chartArea.bottom - chartArea.top;
  if (!gradient || width !== chartWidth || height !== chartHeight) {
    width = chartWidth;
    height = chartHeight;
    gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);

    gradient.addColorStop(1, "#a4c639");
    gradient.addColorStop(0.7, "#3c834d");
    gradient.addColorStop(0.3, "#2a5e38");
    gradient.addColorStop(0, "#ff7e5f");
  }
  return gradient;
}

const Grafico = ({ serial, company, reload, detailsEquip }) => {
  const { configs, equipments } = company;

  const equipamento = useMemo(() => {
    return detailsEquip.find((equip) => equip?.serialnumber === serial);
  }, [serial, detailsEquip]);

  const getColSize = () => {
    if (equipments.length === 1) return 12;
    if (equipments.length <= 4) return 6;
    return 4;
  };

  const getHeightSize = () => {
    if (equipments.length === 1) return 100;
    if (equipments.length === 2) return 140;
    if (equipments.length <= 4) return 80;
    return 160;
  };

  const [medidas, setMedidas] = useState([]);
  const [status, setStatus] = useState(null);
  const [ultimaMedida, setUltimaMedida] = useState({
    _id: "",
    serialnumber: "",
    date: "",
    idcalib: "",
    grain: "",
    humidity: "",
    temperature: "",
    peso: "",
    freqc: "",
    freqv: "",
    ph: "",
    curvavalidade: "",
    companyId: "",
    localEquipament: ""
  });
  const [fardosPorHora, setFardosPorHora] = useState(0);  // Estado para armazenar fardos por hora

  const handleEquipment = async () => {
    try {
      // Buscando dados diretamente do sistema local em vez da API remota
      const response = await Api.get(`/measurements/byEquip/lastn/${serial}/40`);
      // Verifica se a resposta contém um status mensagem de "Não há medidas gravadas"
      if (response.data && response.data.status && response.data.status.includes("Não há medidas")) {
        console.log("Sem medidas disponíveis para este equipamento");
        setMedidas([]);
        return;
      }

      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        // Transformando os dados para o formato esperado
        const formattedData = response.data.map(item => ({
          _id: item.id || "",
          serialnumber: item.serialnumber || serial,
          date: item.date || "",
          humidity: parseFloat(item.humidity || item.umidade || "0"),
          temperature: parseFloat(item.temperature || item.temp || item.temperatura || "0"),
          grain: item.grain || "",
          freqc: item.freqc || "",
          freqv: item.freqv || item.freq_v || item.freq || "",
          ph: item.ph || "0"
        }));

        // Ordenando por data (mais recente primeiro)
        const orderedData = formattedData.sort((a, b) => {
          return new Date(a.date) - new Date(b.date);
        });

        setMedidas(orderedData);
        if (orderedData.length > 0) {
          setUltimaMedida(orderedData[orderedData.length - 1]);
        }
      } else if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        // Se a resposta for um objeto mas não um array, pode ser uma mensagem de erro ou um formato diferente
        console.log("Resposta em formato não esperado:", response.data);
      }
    } catch (error) {
      console.error('Erro ao buscar medidas do sistema local:', error?.response?.data || error?.message);
    }
  };

  const handleGetStatus = async () => {
    try {
      // Consultando status diretamente do sistema local
      const response = await Api.get(`/connection/status/serial/${serial}`);
      setStatus(response.data?.status);
    } catch (error) {
      console.error('Erro ao verificar status do dispositivo:', error?.message);
      setStatus("offline");
    }
  };

  // Função para obter fardos por hora quando for FARD
  const getFardosPorHora = async () => {
    try {
      if (serial.includes("FARD")) {
        const response = await Api.get(`/api/v1/medidas/${serial}/last-hour`);
        if (response.data && response.data.estimativa_medidas_por_hora !== undefined) {
          setFardosPorHora(response.data.estimativa_medidas_por_hora);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar fardos por hora:', error?.message);
    }
  };

  useEffect(() => {
    handleEquipment();
    handleGetStatus();

    // Chamar a função getFardosPorHora se o serial contém "FARD"
    if (serial.includes("FARD")) {
      getFardosPorHora();
    }
  }, [reload, serial]);

  // Verificar se é um dispositivo FARD
  const isFardDevice = serial.includes('FARD');

  const isWithin15Minutes = (lastDateString) => {
    if (!lastDateString) return false;

    try {
      // Garantir formato ISO 8601 adequado
      const dateStr = lastDateString.replace("Z", "");

      const lastDate = new Date(dateStr);
      const now = new Date();

      // Verificar se a data é futura (pode indicar erro no relógio do servidor)
      if (lastDate > now) {
        console.log('Data futura detectada:', lastDateString, '- considerando como recente');
        return true; // Se a data for futura, consideraremos como recente
      }

      // Calcular a diferença em minutos
      const diffMs = now - lastDate;
      const diffMinutes = diffMs / (1000 * 60);

      // Verificar se está dentro dos 20 minutos estabelecidos
      return diffMinutes <= 20;
    } catch (error) {
      console.error('Erro ao processar data:', lastDateString, error);
      return false;
    }
  };

  const isRecent = isWithin15Minutes(ultimaMedida.date);

  return (
    <Col xs={12} sm={12} md={12} lg={12} xl={getColSize()} xxl={getColSize()} className="mb-3">
      <Card className="h-100 shadow dark-card" style={{ borderRadius: '16px', overflow: 'hidden', border: 'none', backgroundColor: '#282c34' }}>
        <Card.Body className="p-3">
          <Row className="g-3 mb-3">
            <Col md={4} className="text-center">
              <h6 className="mb-1 dark-text-secondary" style={{ fontSize: '0.8rem' }}>EQUIPAMENTO</h6>
              {status === "online" ? (
                <Badge bg="success" pill className="mb-2 px-3 py-2">ONLINE</Badge>
              ) : status === "unstable" ? (
                <Badge bg="warning" text="dark" pill className="mb-2 px-3 py-2">INSTÁVEL</Badge>
              ) : (
                <Badge bg="danger" pill className="mb-2 px-3 py-2">OFFLINE</Badge>
              )}

              <h5 className="mb-1 dark-text-accent" style={{ fontWeight: 600 }}>
                {serial || "--"}
              </h5>

              <h6 className="mb-0 dark-text" style={{ fontWeight: 500 }}>
                {equipamento?.nameEquipament?.toUpperCase() || "--"}
              </h6>
            </Col>

            {ultimaMedida?.grain ?
              <Col md={4} className="text-center">
                <h6 className="mb-1 dark-text-secondary" style={{ fontSize: '0.8rem' }}>GRÃO</h6>
                <h5 className="mb-0 dark-text" style={{ fontWeight: 600 }}>
                  {ultimaMedida?.grain?.toUpperCase() || "--"}
                </h5>
              </Col>
              :
              <Col md={4} className="text-center">
              </Col>
            }

            <Col md={4} className="text-center">
              <h6 className="mb-1 dark-text-secondary" style={{ fontSize: '0.8rem' }}>ÚLTIMA MEDIDA</h6>
              <h5 className="mb-0" style={{ color: isRecent ? "#a4c639" : "#ff5252", fontWeight: 600 }}>
                {ultimaMedida?.date ? dateFormat(ultimaMedida.date, "UTC:dd/mm/yy' 'HH:MM") : "--"}
              </h5>
            </Col>
          </Row>

          <Row className="mb-4 g-3">
            <Col md={4}>
              <Card className="mb-0 h-100 shadow-sm dark-card" style={{ borderRadius: '12px', border: 'none', backgroundColor: '#1e2128' }}>
                <Card.Body className="p-3 text-center">
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    <WiHumidity size={30} color="#a4c639" />
                    <h6 style={{ color: "#a4c639", marginBottom: 0, marginLeft: '5px' }}>UMIDADE</h6>
                  </div>
                  <h1 style={{ color: "#e0e0e0", marginBottom: 0, fontWeight: 700 }}>
                    <span style={{ fontSize: "2.0rem" }}>
                      {ultimaMedida?.humidity ? Math.floor(ultimaMedida.humidity) : "--"}
                    </span>
                    <span style={{ fontSize: "1.5rem" }}>
                      {ultimaMedida?.humidity ? "." + (ultimaMedida.humidity % 1).toFixed(1).substring(2) : ""}
                    </span>
                    <span style={{ fontSize: "1.5rem", color: "#a4c639" }}>{ultimaMedida.humidity ? " %" : ""}</span>
                  </h1>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="mb-0 h-100 shadow-sm dark-card" style={{ borderRadius: '12px', border: 'none', backgroundColor: '#1e2128' }}>
                <Card.Body className="p-3 text-center">
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    <WiThermometer size={30} color="#ff7e5f" />
                    <h6 style={{ color: "#ff7e5f", marginBottom: 0, marginLeft: '5px' }}>TEMPERATURA</h6>
                  </div>
                  <h1 style={{ color: "#e0e0e0", marginBottom: 0, fontWeight: 700 }}>
                    <span style={{ fontSize: "2.0rem" }}>
                      {ultimaMedida?.temperature ? Math.floor(ultimaMedida.temperature) : "--"}
                    </span>
                    <span style={{ fontSize: "1.5rem" }}>
                      {ultimaMedida?.temperature && "." + (ultimaMedida.temperature % 1).toFixed(1).substring(2)}
                    </span>
                    <span style={{ fontSize: "1.5rem", color: "#ff7e5f" }}>{ultimaMedida.temperature ? " °C" : ""}</span>
                  </h1>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="mb-0 h-100 shadow-sm dark-card" style={{ borderRadius: '12px', border: 'none', backgroundColor: '#1e2128' }}>
                <Card.Body className="p-3 text-center">
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    <TbPackage size={24} color="#5c9edc" />
                    <h6 style={{ color: "#5c9edc", marginBottom: 0, marginLeft: '5px' }}>
                      {isFardDevice ? "FARDOS/HORA" : "--"}
                    </h6>
                  </div>
                  <h1 style={{ color: "#e0e0e0", marginBottom: 0, fontWeight: 700 }}>
                    {isFardDevice ? (
                      <>
                        <span style={{ fontSize: "2.0rem" }}>
                          {fardosPorHora || "--"}
                        </span>
                        <span style={{ fontSize: "1.2rem", color: "#b0b0b0" }}> fardos/h</span>
                      </>
                    ) : (
                      <span style={{ fontSize: "1.5rem" }}>--</span>
                    )}
                  </h1>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <div style={{ height: '260px', position: 'relative', backgroundColor: '#1e2128', borderRadius: '12px', padding: '10px' }}>
            <Line
              data={{
                labels: medidas.map(data => dateFormat(data.date, "UTC:dd/mm/yy' 'HH:MM")),
                datasets: [
                  {
                    label: `MAX UMIDADE`,
                    data: medidas.map(data => configs?.maxHumidity ? configs?.maxHumidity : null),
                    borderColor: "rgba(255, 82, 82, 0.8)",
                    borderWidth: 2,
                    fill: false,
                    pointRadius: 0,
                    borderDash: [5, 5],
                    tension: 0.1,
                    datalabels: {
                      display: false
                    },
                  },
                  {
                    label: "UMIDADE",
                    backgroundColor: "rgba(60, 131, 77, 0.1)",
                    data: medidas.map((data) => parseFloat(data?.humidity || 0).toFixed(1)),
                    borderColor: function (context) {
                      const chart = context.chart;
                      const { ctx, chartArea } = chart;
                      if (!chartArea) {
                        return;
                      }
                      return getGradient(ctx, chartArea);
                    },
                    borderWidth: 3,
                    fill: true,
                    pointRadius: 3,
                    pointBackgroundColor: "#a4c639",
                    pointBorderColor: "#1e2128",
                    pointBorderWidth: 2,
                    pointHoverRadius: 6,
                    pointHoverBackgroundColor: "#a4c639",
                    pointHoverBorderColor: "#1e2128",
                    pointHoverBorderWidth: 2,
                    tension: 0.3,
                    datalabels: {
                      align: 'end',
                      offset: -2,
                      display: function(context) {
                        return context.dataIndex % 5 === 0;
                      }
                    },
                  },
                  {
                    label: `MIN UMIDADE`,
                    data: medidas.map(data => configs?.minHumidity ? configs?.minHumidity : null),
                    borderColor: "rgba(255, 193, 7, 0.7)",
                    borderWidth: 2,
                    fill: false,
                    pointRadius: 0,
                    borderDash: [5, 5],
                    tension: 0.1,
                    datalabels: {
                      display: false
                    },
                  }
                ],
              }}
              plugins={[plugins]}
              options={options}
            />
          </div>
        </Card.Body>
      </Card>
    </Col >
  );
};

export default Grafico;
